import { http, json } from "utils/http/client";

function getOrgId() {
  try {
    const org = JSON.parse(localStorage.getItem("organization") || "{}");
    return org?.id ? Number(org.id) : null;
  } catch (_) {
    return null;
  }
}

export const LeadAPI = {
  orgId: getOrgId,

  /**
   * GET /lead/org/{org_id}/leads/
   */
  listLeads: (orgId) => {
    if (!orgId) throw new Error("Organization id not found");
    return json(`/lead/org/${orgId}/leads/`, { method: "GET" });
  },

  /**
   * GET /lead/org/{org_id}/leads/{pk}/
   */
  getLead: (orgId, leadId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/`, { method: "GET" });
  },

  /**
   * PATCH /lead/org/{org_id}/leads/{lead_id}/
   * payload: { name?, phone?, email?, city? }
   */
  updateLead: (orgId, leadId, payload) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/`, {
      method: "PATCH",
      body: payload,
    });
  },

  /**
   * POST /lead/org/{org_id}/leads/
   * Expected payload (example):
   * {
   *   name, phone, email?, city,
   *   project_type, property_size, budget, start_date, remarks,
   *   status, organization
   * }
   */
  createLead: (orgId, payload) => {
    if (!orgId) throw new Error("Organization id not found");
    return json(`/lead/org/${orgId}/leads/`, { method: "POST", body: payload });
  },

  /**
   * GET /lead/org/{org_id}/leads/{lead_id}/activities/
   */
  listActivities: (orgId, leadId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/activities/`, {
      method: "GET",
    });
  },

  /**
   * POST /lead/org/{org_id}/leads/{lead_id}/activities/
   * payload: { description: string, lead: number }
   */
  createActivity: (orgId, leadId, payload) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/activities/`, {
      method: "POST",
      body: payload,
    });
  },

  /**
   * GET /lead/org/{org_id}/leads/{lead_id}/activities/{pk}/
   */
  getActivity: (orgId, leadId, activityId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    if (!activityId && activityId !== 0) throw new Error("Activity id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/activities/${activityId}/`, {
      method: "GET",
    });
  },

  /**
   * PATCH /lead/org/{org_id}/leads/{lead_id}/activities/{pk}/
   */
  updateActivity: (orgId, leadId, activityId, payload) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    if (!activityId && activityId !== 0) throw new Error("Activity id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/activities/${activityId}/`, {
      method: "PATCH",
      body: payload,
    });
  },

  /**
   * DELETE /lead/org/{org_id}/leads/{lead_id}/activities/{pk}/
   */
  deleteActivity: (orgId, leadId, activityId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    if (!activityId && activityId !== 0) throw new Error("Activity id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/activities/${activityId}/`, {
      method: "DELETE",
    });
  },

  /**
   * GET /lead/org/{org_id}/leads/{lead_id}/notes/
   */
  listNotes: (orgId, leadId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/notes/`, { method: "GET" });
  },

  /**
   * POST /lead/org/{org_id}/leads/{lead_id}/notes/
   * payload: { text: string, lead: number }
   */
  createNote: (orgId, leadId, payload) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/notes/`, {
      method: "POST",
      body: payload,
    });
  },

  /**
   * GET /lead/org/{org_id}/leads/{lead_id}/notes/{pk}/
   */
  getNote: (orgId, leadId, noteId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    if (!noteId && noteId !== 0) throw new Error("Note id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/notes/${noteId}/`, { method: "GET" });
  },

  /**
   * PATCH /lead/org/{org_id}/leads/{lead_id}/notes/{pk}/
   */
  updateNote: (orgId, leadId, noteId, payload) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    if (!noteId && noteId !== 0) throw new Error("Note id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/notes/${noteId}/`, {
      method: "PATCH",
      body: payload,
    });
  },

  /**
   * DELETE /lead/org/{org_id}/leads/{lead_id}/notes/{pk}/
   */
  deleteNote: (orgId, leadId, noteId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    if (!noteId && noteId !== 0) throw new Error("Note id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/notes/${noteId}/`, {
      method: "DELETE",
    });
  },

  /**
   * GET /lead/org/{org_id}/leads/{lead_id}/meetings/
   */
  listMeetings: (orgId, leadId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/meetings/`, { method: "GET" });
  },

  /**
   * POST /lead/org/{org_id}/leads/{lead_id}/meetings/
   * payload: { lead: number, title: string, description?: string, date: string, time: string, link?: string }
   */
  createMeeting: (orgId, leadId, payload) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/meetings/`, {
      method: "POST",
      body: payload,
    });
  },

  /**
   * GET /lead/org/{org_id}/leads/{lead_id}/meetings/{pk}/
   */
  getMeeting: (orgId, leadId, meetingId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    if (!meetingId && meetingId !== 0) throw new Error("Meeting id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/meetings/${meetingId}/`, { method: "GET" });
  },

  /**
   * PATCH /lead/org/{org_id}/leads/{lead_id}/meetings/{pk}/
   */
  updateMeeting: (orgId, leadId, meetingId, payload) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    if (!meetingId && meetingId !== 0) throw new Error("Meeting id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/meetings/${meetingId}/`, {
      method: "PATCH",
      body: payload,
    });
  },

  /**
   * DELETE /lead/org/{org_id}/leads/{lead_id}/meetings/{pk}/
   */
  deleteMeeting: (orgId, leadId, meetingId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    if (!meetingId && meetingId !== 0) throw new Error("Meeting id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/meetings/${meetingId}/`, {
      method: "DELETE",
    });
  },

  /**
   * GET /lead/org/{org_id}/leads/{lead_id}/scope/
   */
  getScope: (orgId, leadId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/scope/`, { method: "GET" });
  },

  /**
   * POST /lead/org/{org_id}/leads/{lead_id}/scope/
   * payload: { name, tentative_start, property_size, budget, status }
   */
  createScope: (orgId, leadId, payload) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/scope/`, {
      method: "POST",
      body: payload,
    });
  },

  updateScope: (orgId, leadId, payload) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/scope/`, {
      method: "PATCH",
      body: payload,
    });
  },


  /**
   * POST /lead/org/{org_id}/leads/{lead_id}/scope/send/
   */
  sendScopeForApproval: (orgId, leadId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/scope/send/`, { method: "PATCH" });
  },

  /**
   * POST /lead/org/{org_id}/leads/{lead_id}/scope/approve/approver/
   */
  approveScopeAsApprover: (orgId, leadId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/scope/approve/approver/`, { method: "PATCH" });
  },

  /**
   * PATCH /lead/org/{org_id}/leads/{lead_id}/scope/reject/approver/
   */
  rejectScopeAsApprover: (orgId, leadId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/scope/reject/approver/`, { method: "PATCH" });
  },

  /**
   * POST /lead/org/{org_id}/leads/{lead_id}/scope/approve/client/
   */
  approveScopeAsClient: (orgId, leadId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/scope/approve/client/`, { method: "PATCH" });
  },

  /**
   * POST /lead/org/{org_id}/leads/{lead_id}/scope/reopen/
   */
  reopenScope: (orgId, leadId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/scope/reopen/`, { method: "PATCH" });
  },

  /**
   * POST /lead/org/{org_id}/leads/{lead_id}/scope/categories/
   * payload: { name: string }
   */
  createScopeCategory: (orgId, leadId, payload) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/scope/categories/`, {
      method: "POST",
      body: payload,
    });
  },

  /**
   * GET /lead/org/{org_id}/leads/{lead_id}/scope/categories/{pk}/
   */
  getScopeCategory: (orgId, leadId, categoryId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    if (!categoryId && categoryId !== 0) throw new Error("Category id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/scope/categories/${categoryId}/`, {
      method: "GET",
    });
  },

  /**
   * PATCH /lead/org/{org_id}/leads/{lead_id}/scope/categories/{pk}/
   */
  updateScopeCategory: (orgId, leadId, categoryId, payload) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    if (!categoryId && categoryId !== 0) throw new Error("Category id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/scope/categories/${categoryId}/`, {
      method: "PATCH",
      body: payload,
    });
  },

  /**
   * DELETE /lead/org/{org_id}/leads/{lead_id}/scope/categories/{pk}/
   */
  deleteScopeCategory: (orgId, leadId, categoryId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    if (!categoryId && categoryId !== 0) throw new Error("Category id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/scope/categories/${categoryId}/`, {
      method: "DELETE",
    });
  },

  /**
   * GET /lead/org/{org_id}/leads/{lead_id}/scope/full/
   * Response example: { success: true, data: { ...scope } }
   */
  getScopeFull: (orgId, leadId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/scope/full/`, { method: "GET" });
  },

  /**
   * GET /lead/org/{org_id}/leads/{lead_id}/scope/versions/
   */
  listScopeVersions: (orgId, leadId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/scope/versions/`, { method: "GET" });
  },

  /**
   * GET /lead/org/{org_id}/leads/{lead_id}/scope/versions/{version_id}/
   */
  getScopeVersion: (orgId, leadId, versionId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    if (!versionId && versionId !== 0) throw new Error("Version id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/scope/versions/${versionId}/`, {
      method: "GET",
    });
  },

  /**
   * POST /lead/org/{org_id}/leads/{lead_id}/scope/categories/{category_id}/requirements/
   * payload: { field, type, value }
   */
  createRequirement: (orgId, leadId, categoryId, payload) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    if (!categoryId && categoryId !== 0) throw new Error("Category id not found");
    return json(
      `/lead/org/${orgId}/leads/${leadId}/scope/categories/${categoryId}/requirements/`,
      { method: "POST", body: payload }
    );
  },

  /**
   * PATCH /lead/org/{org_id}/leads/{lead_id}/scope/requirements/{pk}/
   */
  updateRequirement: (orgId, leadId, requirementId, payload) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    if (!requirementId && requirementId !== 0) throw new Error("Requirement id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/scope/requirements/${requirementId}/`, {
      method: "PATCH",
      body: payload,
    });
  },

  /**
   * DELETE /lead/org/{org_id}/leads/{lead_id}/scope/requirements/{pk}/
   */
  deleteRequirement: (orgId, leadId, requirementId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    if (!requirementId && requirementId !== 0) throw new Error("Requirement id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/scope/requirements/${requirementId}/`, {
      method: "DELETE",
    });
  },

  /**
   * POST /lead/org/{org_id}/leads/{lead_id}/scope/requirements/{requirement_id}/images/
   * form-data: image + requirement
   */
  uploadRequirementImage: async (orgId, leadId, requirementId, file) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    if (!requirementId && requirementId !== 0) throw new Error("Requirement id not found");
    if (!file) throw new Error("Image file not found");

    const form = new FormData();
    form.append("image", file);
    form.append("requirement", String(requirementId));

    const res = await http(
      `/lead/org/${orgId}/leads/${leadId}/scope/requirements/${requirementId}/images/`,
      { method: "POST", body: form }
    );

    let data = null;
    try {
      data = await res.json();
    } catch (_) {
      // ignore
    }
    if (!res.ok) {
      const err = new Error(data?.detail || data?.message || res.statusText);
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  },

  /**
   * GET /lead/org/{org_id}/leads/{lead_id}/scope/{scope_id}/feedback/
   */
  listScopeFeedback: (orgId, leadId, scopeId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    if (!scopeId && scopeId !== 0) throw new Error("Scope id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/scope/${scopeId}/feedback/`, {
      method: "GET",
    });
  },

  /**
   * POST /lead/org/{org_id}/leads/{lead_id}/scope/{scope_id}/feedback/
   * payload: { scope: number, message: string }
   */
  createScopeFeedback: (orgId, leadId, scopeId, payload) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    if (!scopeId && scopeId !== 0) throw new Error("Scope id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/scope/${scopeId}/feedback/`, {
      method: "POST",
      body: payload,
    });
  },

  /**
   * PATCH /lead/org/{org_id}/leads/{lead_id}/scope/{scope_id}/feedback/{pk}/
   */
  updateScopeFeedback: (orgId, leadId, scopeId, feedbackId, payload) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    if (!scopeId && scopeId !== 0) throw new Error("Scope id not found");
    if (!feedbackId && feedbackId !== 0) throw new Error("Feedback id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/scope/${scopeId}/feedback/${feedbackId}/`, {
      method: "PATCH",
      body: payload,
    });
  },

  /**
   * DELETE /lead/org/{org_id}/leads/{lead_id}/scope/{scope_id}/feedback/{pk}/
   */
  deleteScopeFeedback: (orgId, leadId, scopeId, feedbackId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    if (!scopeId && scopeId !== 0) throw new Error("Scope id not found");
    if (!feedbackId && feedbackId !== 0) throw new Error("Feedback id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/scope/${scopeId}/feedback/${feedbackId}/`, {
      method: "DELETE",
    });
  },

  /**
   * GET /lead/org/{org_id}/leads/{lead_id}/quotation/
   */
  getQuotation: (orgId, leadId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/quotation/`, { method: "GET" });
  },

  /**
   * POST /lead/org/{org_id}/leads/{lead_id}/quotation/
   * payload: { scope, name, payment_terms, terms_conditions }
   */
  createQuotation: (orgId, leadId, payload) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/quotation/`, {
      method: "POST",
      body: payload,
    });
  },

  /**
   * PATCH /lead/org/{org_id}/leads/{lead_id}/quotation/
   * payload: { payment_terms?, terms_conditions? }
   */
  updateQuotation: (orgId, leadId, payload) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/quotation/`, {
      method: "PATCH",
      body: payload,
    });
  },

  /**
   * GET /lead/org/{org_id}/leads/{lead_id}/quotation/full/
   * Response example: { success: true, data: { ...quotation } }
   */
  getQuotationFull: (orgId, leadId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/quotation/full/`, { method: "GET" });
  },

  /**
   * GET /lead/org/{org_id}/leads/{lead_id}/quotation/versions/
   */
  listQuotationVersions: (orgId, leadId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/quotation/versions/`, { method: "GET" });
  },

  /**
   * PATCH /lead/org/{org_id}/leads/{lead_id}/quotation/send/
   */
  sendQuotationForApproval: (orgId, leadId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/quotation/send/`, { method: "PATCH" });
  },

  /**
   * PATCH /lead/org/{org_id}/leads/{lead_id}/quotation/approve/approver/
   */
  approveQuotationAsApprover: (orgId, leadId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/quotation/approve/approver/`, { method: "PATCH" });
  },

  /**
   * PATCH /lead/org/{org_id}/leads/{lead_id}/quotation/reject/approver/
   */
  rejectQuotationAsApprover: (orgId, leadId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/quotation/reject/approver/`, { method: "PATCH" });
  },

  /**
   * PATCH /lead/org/{org_id}/leads/{lead_id}/quotation/approve/client/
   */
  approveQuotationAsClient: (orgId, leadId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/quotation/approve/client/`, { method: "PATCH" });
  },

  /**
   * PATCH /lead/org/{org_id}/leads/{lead_id}/quotation/reopen/
   */
  reopenQuotation: (orgId, leadId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/quotation/reopen/`, { method: "PATCH" });
  },

  /**
   * PATCH /lead/org/{org_id}/leads/{lead_id}/quotation/lock
   */
  lockQuotation: (orgId, leadId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/quotation/lock/`, { method: "PATCH" });
  },

  /**
   * POST /lead/org/{org_id}/leads/{lead_id}/convert/
   * payload: { site_name: string, esrimated_cost: number }
   */
  convertLeadToProject: (orgId, leadId, payload) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/convert/`, { method: "POST", body: payload });
  },

  /**
   * POST /lead/org/{org_id}/leads/{lead_id}/quotation/items/
   * payload: { quotation, name, price, tax }
   */
  createQuotationItem: (orgId, leadId, payload) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/quotation/items/`, {
      method: "POST",
      body: payload,
    });
  },

  /**
   * PATCH /lead/org/{org_id}/leads/{lead_id}/quotation/items/{pk}/
   */
  updateQuotationItem: (orgId, leadId, itemId, payload) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    if (!itemId && itemId !== 0) throw new Error("Item id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/quotation/items/${itemId}/`, {
      method: "PATCH",
      body: payload,
    });
  },

  /**
   * DELETE /lead/org/{org_id}/leads/{lead_id}/quotation/items/{pk}/
   */
  deleteQuotationItem: (orgId, leadId, itemId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    if (!itemId && itemId !== 0) throw new Error("Item id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/quotation/items/${itemId}/`, {
      method: "DELETE",
    });
  },

  /**
   * POST /lead/org/{org_id}/leads/{lead_id}/quotation/items/{item_id}/subitems/
   * payload: { item, name, price, tax }
   */
  createQuotationSubitem: (orgId, leadId, itemId, payload) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    if (!itemId && itemId !== 0) throw new Error("Item id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/quotation/items/${itemId}/subitems/`, {
      method: "POST",
      body: payload,
    });
  },

  /**
   * PATCH /lead/org/{org_id}/leads/{lead_id}/quotation/subitems/{pk}/
   */
  updateQuotationSubitem: (orgId, leadId, subitemId, payload) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    if (!subitemId && subitemId !== 0) throw new Error("Subitem id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/quotation/subitems/${subitemId}/`, {
      method: "PATCH",
      body: payload,
    });
  },

  /**
   * DELETE /lead/org/{org_id}/leads/{lead_id}/quotation/subitems/{pk}/
   */
  deleteQuotationSubitem: (orgId, leadId, subitemId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    if (!subitemId && subitemId !== 0) throw new Error("Subitem id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/quotation/subitems/${subitemId}/`, {
      method: "DELETE",
    });
  },

  /**
   * GET /lead/org/{org_id}/leads/{lead_id}/quotation/customcharges/
   */
  listQuotationCustomCharges: (orgId, leadId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/quotation/customcharges/`, { method: "GET" });
  },

  /**
   * POST /lead/org/{org_id}/leads/{lead_id}/quotation/customcharges/
   * payload: { label, amount }
   */
  createQuotationCustomCharge: (orgId, leadId, payload) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/quotation/customcharges/`, {
      method: "POST",
      body: payload,
    });
  },

  /**
   * GET /lead/org/{org_id}/leads/{lead_id}/quotation/feedback/
   */
  listQuotationFeedback: (orgId, leadId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/quotation/feedback/`, { method: "GET" });
  },

  /**
   * POST /lead/org/{org_id}/leads/{lead_id}/quotation/feedback/
   * payload: { message }
   */
  createQuotationFeedback: (orgId, leadId, payload) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/quotation/feedback/`, {
      method: "POST",
      body: payload,
    });
  },

  /**
   * PATCH /lead/org/{org_id}/leads/{lead_id}/quotation/feedback/{pk}/
   */
  updateQuotationFeedback: (orgId, leadId, feedbackId, payload) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    if (!feedbackId && feedbackId !== 0) throw new Error("Feedback id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/quotation/feedback/${feedbackId}/`, {
      method: "PATCH",
      body: payload,
    });
  },

  /**
   * DELETE /lead/org/{org_id}/leads/{lead_id}/quotation/feedback/{pk}/
   */
  deleteQuotationFeedback: (orgId, leadId, feedbackId) => {
    if (!orgId) throw new Error("Organization id not found");
    if (!leadId && leadId !== 0) throw new Error("Lead id not found");
    if (!feedbackId && feedbackId !== 0) throw new Error("Feedback id not found");
    return json(`/lead/org/${orgId}/leads/${leadId}/quotation/feedback/${feedbackId}/`, {
      method: "DELETE",
    });
  },
  /**
   * GET /lead/org/{org_id}/recent-activities/
   */
  getRecentActivities: (orgId) => {
    if (!orgId) throw new Error("Organization id not found");
    return json(`/lead/org/${orgId}/recent-activities/`, { method: "GET" });
  },
};


